// Admin Volunteers Management Page
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import GlassCard from '../../components/admin/GlassCard';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminVolunteers: React.FC = () => {
  const [volunteers, setVolunteers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    bio: '',
    image_url: '',
    origin: 'Own',
    country: 'Nepal',
    year: new Date().getFullYear().toString()
  });
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [uploading, setUploading] = React.useState(false);

  useEffect(() => {
    fetchVolunteers();
  }, []);

  const fetchVolunteers = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/volunteers');
      const data = await response.json();
      console.log('Volunteers data:', data);
      setVolunteers(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = formData.image_url;

      // Upload image if a new file is selected
      if (imageFile) {
        console.log('Uploading image:', imageFile.name, 'Type:', imageFile.type, 'Size:', imageFile.size);
        const uploadFormData = new FormData();
        uploadFormData.append('image', imageFile);
        uploadFormData.append('category', 'volunteers');

        try {
          const uploadResponse = await fetch('http://localhost:5000/api/upload', {
            method: 'POST',
            body: uploadFormData
          });

          console.log('Upload response status:', uploadResponse.status, uploadResponse.statusText);

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({ error: 'Unknown server error' }));
            console.error('Upload failed:', errorData);
            throw new Error(errorData.error || 'Image upload failed');
          }

          const uploadResult = await uploadResponse.json();
          console.log('Upload successful:', uploadResult);
          imageUrl = uploadResult.url;
        } catch (uploadError) {
          console.error('Network or upload error:', uploadError);
          throw new Error(`Upload failed: ${uploadError instanceof Error ? uploadError.message : 'Network error'}`);
        }
      }

      const url = editingVolunteer
        ? `http://localhost:5000/api/volunteers/${editingVolunteer.id}`
        : 'http://localhost:5000/api/volunteers';
      
      const volunteerData = {
        name: formData.name,
        role: formData.role,
        bio: formData.bio,
        image: imageUrl || '',
        origin: formData.origin,
        country: formData.country,
        year: parseInt(formData.year) || new Date().getFullYear(),
        order_position: 0,
        active: true
      };
      
      console.log('Sending volunteer data:', JSON.stringify(volunteerData, null, 2));
      
      const response = await fetch(url, {
        method: editingVolunteer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(volunteerData)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response (full text):', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          console.error('Parsed error data:', JSON.stringify(errorData, null, 2));
        } catch {
          errorData = { error: 'Server error', details: errorText };
          console.error('Could not parse error as JSON');
        }
        alert(`Failed to save volunteer: ${errorData.error || 'Unknown error'}\nDetails: ${errorData.details || errorText.substring(0, 200)}`);
        throw new Error(errorData.error || 'Failed to save volunteer');
      }

      const result = await response.json();
      console.log('Save successful:', result);

      if (response.ok) {
        fetchVolunteers();
        setShowAddForm(false);
        setEditingVolunteer(null);
        setImageFile(null);
        setImagePreview('');
        setFormData({
          name: '',
          role: '',
          bio: '',
          image_url: '',
          origin: 'Own',
          country: 'Nepal',
          year: new Date().getFullYear().toString()
        });
      }
    } catch (error) {
      console.error('Error saving volunteer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (!errorMessage.includes('Failed to save')) {
        alert('Failed to save volunteer. Please check console for details.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (volunteer: any) => {
    setEditingVolunteer(volunteer);
    setFormData({
      name: volunteer.name,
      role: volunteer.role,
      bio: volunteer.bio,
      image_url: volunteer.image,
      origin: volunteer.origin || 'Own',
      country: volunteer.country || 'Nepal',
      year: volunteer.year ? volunteer.year.toString() : new Date().getFullYear().toString()
    });
    setImagePreview(volunteer.image || '');
    setImageFile(null);
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this volunteer?')) {
      try {
        await fetch(`http://localhost:5000/api/volunteers/${id}`, {
          method: 'DELETE'
        });
        fetchVolunteers();
      } catch (error) {
        console.error('Error deleting volunteer:', error);
      }
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'origin', label: 'Program', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'year', label: 'Year', sortable: true },
    {
      key: 'bio',
      label: 'Bio',
      render: (value: string) => value ? value.substring(0, 40) + '...' : ''
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
            Volunteers Management
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
            Manage volunteers and their profiles
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingVolunteer(null);
            setFormData({
              name: '',
              role: '',
              bio: '',
              image_url: '',
              origin: 'Own',
              country: 'Nepal',
              year: new Date().getFullYear().toString()
            });
          }}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#111827',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          {showAddForm ? '✕ Cancel' : '+ Add Volunteer'}
        </button>
      </div>

      {showAddForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            {editingVolunteer ? 'Edit Volunteer' : 'Add New Volunteer'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Role
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  placeholder="e.g., Community Volunteer"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Program Origin
                </label>
                <select
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                >
                  <option value="Own">Own (Direct)</option>
                  <option value="Fly Program">Fly Program</option>
                  <option value="University">University</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  placeholder="e.g., Nepal, USA, UK"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#111827',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Profile Image (370 x 451px)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              {imagePreview && (
                <div style={{ marginTop: '12px' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '185px',
                      height: '225.5px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB'
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: '#111827',
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={uploading}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                background: uploading ? '#9CA3AF' : '#111827',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.7 : 1
              }}
            >
              {uploading ? 'Uploading...' : (editingVolunteer ? 'Update Volunteer' : 'Add Volunteer')}
            </button>
          </form>
        </GlassCard>
      )}

      <DataTable
        columns={columns}
        data={volunteers}
        actions={(row) => (
          <>
            <button
              onClick={() => handleEdit(row)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #DBEAFE',
                background: '#EFF6FF',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #FEE2E2',
                background: '#FEF2F2',
                color: '#DC2626',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </>
        )}
      />
    </AdminLayout>
  );
};

export default AdminVolunteers;
